//  This document handles the logic for the wishlist and the hearts
//  Logged-in customers: the wishlist is read from the custom.wishlist metafield,
//  exposed as window.CUSTOMER.wishlist in theme.liquid on every page load.
//  Add/remove go to the oace-wishlist-v2 API, which updates the metafield behind
//  the scenes; localStorage (iWishlistmain) mirrors the state for the hearts.
//  Guests (no window.CUSTOMER.id) keep a pure localStorage wishlist (iWishlistmainNotLoggedin).

const WISHLIST_DOMAIN = "https://api.tante-e.com";
const WISHLIST_API = `${WISHLIST_DOMAIN}/oace-wishlist-v2/wishlist`;

// Internal item shape: { id, handle, variantId, hasExplicitVariant }
// Tolerates the api/metafield shape ({ productId, variantId, handle, hasExplicitVariant })
// and the legacy localStorage shape ({ id, handle, variant: { id } }).
const normalizeWishlistItem = (item) => {
  const variantId =
    item.variantId ??
    item.variant_id ??
    (item.variant && item.variant.id) ??
    null;
  return {
    id: String(item.productId ?? item.product_id ?? item.id ?? ""),
    handle: item.handle,
    variantId: variantId ? String(variantId) : null,
    hasExplicitVariant: item.hasExplicitVariant ?? Boolean(variantId),
  };
};

// The metafield and the GET endpoint both hold an array of wishlists,
// each with a products array. Flatten and dedupe them into one item list.
const normalizeWishlists = (wishlists) => {
  const products = (Array.isArray(wishlists) ? wishlists : []).flatMap(
    (list) => list.products || []
  );
  const seen = new Set();
  return products.map(normalizeWishlistItem).filter((item) => {
    const key = `${item.id}:${item.variantId ?? ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const fetchProductCardHTML = (url) => {
  return fetch(url)
    .then((res) => res.text())
    .then((res) => {
      const text = res;
      const parser = new DOMParser();
      const htmlDocument = parser.parseFromString(text, "text/html");
      const productCardWrapper = document.createElement("div");
      productCardWrapper.className = "grid__item";
      const productCard =
        htmlDocument.documentElement.querySelector(".product-card");
      productCardWrapper.appendChild(productCard);
      return productCardWrapper.outerHTML;
    })
    .catch((err) =>
      console.error(
        `[Shopify Wishlist] Failed to load content for url: ${url}`,
        err
      )
    );
};
async function createWishlistCards(result) {
  if (
    result.length > 0 &&
    document.querySelector(".collection-wishlist .empty-state-webshop")
  ) {
    document.querySelector(
      ".collection-wishlist .empty-state-webshop"
    ).style.display = "none";
  }
  let productUrls = [];
  result.forEach((item) => {
    let url = `/products/${item.handle}?view=card`;
    if (item.variantId) url += `&variant=${item.variantId}`;
    productUrls.push(url);
  });
  const requests = productUrls.map(fetchProductCardHTML);
  const responses = await Promise.all(requests);
  const wishlistProductCards = responses.join("");
  document.querySelector("#wishlist-container").innerHTML =
    wishlistProductCards;
}
if (window.CUSTOMER && window.CUSTOMER.id) {
  try {
    const activeItems = normalizeWishlists(window.CUSTOMER.wishlist);
    localStorage.setItem("iWishlistmain", JSON.stringify(activeItems));
    // Drop the keys from the retired hourly API-fetch cache
    localStorage.removeItem("iWishlistSyncedAt");
    localStorage.removeItem("iWishUser");

    if (document.querySelector("#wishlist-container"))
      createWishlistCards(activeItems);
    if (activeItems.length > 0) {
      document
        .querySelector(".header__nav-icon-wish-span")
        .removeAttribute("hidden");
      document.querySelector(".header__nav-icon-wish-span").innerHTML =
        activeItems.length;
    } else {
      document
        .querySelector(".header__nav-icon-wish-span")
        .setAttribute("hidden", "");
    }
  } catch (error) {
    console.error(error);
  }
} else {
  const guestItems = (
    JSON.parse(localStorage.getItem("iWishlistmainNotLoggedin")) || []
  ).map(normalizeWishlistItem);
  localStorage.setItem("iWishlistmainNotLoggedin", JSON.stringify(guestItems));
  if (guestItems.length > 0) {
    if (document.querySelector("#wishlist-container"))
      createWishlistCards(guestItems);
    document
      .querySelector(".header__nav-icon-wish-span")
      .removeAttribute("hidden");
    document.querySelector(".header__nav-icon-wish-span").innerHTML =
      guestItems.length;
  } else {
    document
      .querySelector(".header__nav-icon-wish-span")
      .setAttribute("hidden", "");
  }
}

// handle the hearts for add and remove the product to or from the wishlist
// there are two functions for adding and deleting products.
//          - One that sets and removes the events (removeToWishlistEvent() and addToWishlistEvent()) and
//          - one that does the actual logic (handleRemoveFromWishlist, handleAddToWishlist)
// For the logic on the PPD we need to add and delete from wishlist
// For product card we need to open the drawer or remove from wishlist
// For drawer we can only add to wishlist

class WishHeart extends HTMLElement {
  constructor() {
    super();
    if (this.closest("product-card"))
      this.banner = this.closest("product-card").querySelector(
        ".add-to-wish-banner"
      );
    document.addEventListener("variant:change", (e) => {
      if (e.detail.product.id == this.dataset.productId) {
        this.setAttribute("data-variant-id", e.detail.variant.id);
        this.init();
      }
    });
    this.init();
  }

  getStoredWishlist() {
    const name =
      window.CUSTOMER && window.CUSTOMER.id
        ? "iWishlistmain"
        : "iWishlistmainNotLoggedin";
    const stored = JSON.parse(localStorage.getItem(name));
    return Array.isArray(stored) ? stored : [];
  }

  // PDP hearts match on the exact variant, or on a product-level wish
  // (hasExplicitVariant false covers every variant). Card hearts match on the product.
  findWishlistItem(items) {
    if (this.classList.contains("wish-heart_product-info")) {
      return (
        items.find(
          (obj) => obj.variantId && obj.variantId == this.dataset.variantId
        ) ||
        items.find(
          (obj) => obj.id == this.dataset.productId && !obj.hasExplicitVariant
        )
      );
    }
    return items.find((obj) => obj.id == this.dataset.productId);
  }

  async handleFetchAdd(product_id, variant_id) {
    const body = { customer_id: window.CUSTOMER.id, product_id };
    if (variant_id) body.variant_id = variant_id;
    try {
      await fetch(WISHLIST_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then((r) => r.json());
      this.handleAddLocalStorage("iWishlistmain");
    } catch (error) {
      console.error(error);
    }
  }

  async handleFetchRemove(product_id, variant_id) {
    let url = `${WISHLIST_API}?customer_id=${window.CUSTOMER.id}&product_id=${product_id}`;
    if (variant_id) url += `&variant_id=${variant_id}`;
    try {
      await fetch(url, { method: "DELETE" }).then((r) => r.json());
      this.handleRemoveLocalStorage("iWishlistmain");
    } catch (error) {
      console.error(error);
    }
  }

  handleAddLocalStorage(name) {
    const localStorageData = JSON.parse(localStorage.getItem(name));
    const newItem = {
      id: this.dataset.productId,
      handle: this.dataset.handle,
      variantId: this.dataset.variantId || null,
      hasExplicitVariant: Boolean(this.dataset.variantId),
    };
    let newLocalStorageData = [];
    if (localStorageData) {
      newLocalStorageData = localStorageData.concat(newItem);
    } else {
      newLocalStorageData = [newItem];
    }
    localStorage.setItem(name, JSON.stringify(newLocalStorageData));
    this.updateSpanNumber(JSON.parse(localStorage.getItem(name)).length);
  }

  handleRemoveLocalStorage(name) {
    const localStorageData = JSON.parse(localStorage.getItem(name)) || [];
    let updatedArray;
    if (this.classList.contains("wish-heart_product-info")) {
      updatedArray = localStorageData.filter(
        (obj) =>
          !(
            (obj.variantId && obj.variantId == this.dataset.variantId) ||
            (obj.id == this.dataset.productId && !obj.hasExplicitVariant)
          )
      );
    } else {
      updatedArray = localStorageData.filter(
        (obj) => obj.id != this.dataset.productId
      );
    }
    localStorage.setItem(name, JSON.stringify(updatedArray));
    this.updateSpanNumber(updatedArray.length);
  }

  updateSpanNumber(length) {
    const spanNumber = document.querySelector(".header__nav-icon-wish-span");
    spanNumber.innerText = length;

    if (length > 0) {
      spanNumber.removeAttribute("hidden");
    } else {
      spanNumber.setAttribute("hidden", "");
      if (document.querySelector(".collection-wishlist .empty-state-webshop")) {
        document.querySelector(
          ".collection-wishlist .empty-state-webshop"
        ).style.display = "block";
      }
    }
  }

  addToWishlistEvent() {
    if (
      this.classList.contains("wish-heart-product_card") &&
      this.closest("product-card") &&
      this.closest("product-card").querySelector("quick-buy-drawer")
    ) {
      this.addEventListener("click", this.openDrawer);
    } else {
      this.addEventListener("click", this.handleAddToWishlist);
      this.removeEventListener("click", this.handleRemoveFromWishlist);
    }
  }

  openDrawer() {
    this.closest("product-card")
      .querySelector("quick-buy-drawer")
      .classList.add("wish-drawer");
    this.closest("product-card").querySelector("quick-buy-drawer").show();
  }

  removeToWishlistEvent() {
    this.addEventListener("click", this.handleRemoveFromWishlist);
    this.removeEventListener("click", this.handleAddToWishlist);
  }

  handleAddToWishlist = async () => {
    if (window.CUSTOMER && window.CUSTOMER.id) {
      await this.handleFetchAdd(
        Number(this.dataset.productId),
        this.dataset.variantId ? Number(this.dataset.variantId) : undefined
      );
    } else {
      this.handleAddLocalStorage("iWishlistmainNotLoggedin");
    }

    document
      .querySelectorAll(
        `wish-heart.heart-icon[data-handle="${this.getAttribute(
          "data-handle"
        )}"]`
      )
      .forEach((heart) => (heart.querySelector("svg").style.fill = "#000"));

    if (this.closest("quick-buy-drawer")) {
      this.closest("quick-buy-drawer").hide();
      const heartOnCard = document.querySelector(
        `wish-heart[data-product-id="${this.dataset.productId}"]`
      );
      await heartOnCard.init();
      heartOnCard.removeEventListener("click", heartOnCard.openDrawer);
    } else {
      this.removeToWishlistEvent();
    }
  };

  handleRemoveFromWishlist = () => {
    if (window.CUSTOMER && window.CUSTOMER.id) {
      // Product-level wishes (no explicit variant) must be deleted without a variant_id
      const item = this.findWishlistItem(this.getStoredWishlist());
      const variantId =
        item && item.hasExplicitVariant
          ? item.variantId
          : item
          ? null
          : this.dataset.variantId;
      this.handleFetchRemove(
        Number(this.dataset.productId),
        variantId ? Number(variantId) : undefined
      );
    } else {
      this.handleRemoveLocalStorage("iWishlistmainNotLoggedin");
    }
    document
      .querySelectorAll(
        `wish-heart.heart-icon[data-handle="${this.getAttribute(
          "data-handle"
        )}"]`
      )
      .forEach((heart) => (heart.querySelector("svg").style.fill = "none"));

    if (this.banner) this.banner.style.display = "none";
    if (document.querySelector("#wishlist-container"))
      this.closest(".grid__item").remove();
    this.addToWishlistEvent();
  };

  init() {
    const wishlistItems = this.getStoredWishlist();
    const productWish = this.findWishlistItem(wishlistItems);
    if (
      productWish &&
      productWish.variantId &&
      !this.classList.contains("wish-heart_product-info")
    ) {
      this.dataset.variantId = productWish.variantId;
    }

    if (productWish) {
      this.removeToWishlistEvent();
      if (this.querySelector("svg"))
        this.querySelector("svg").style.fill = "#000";
    } else {
      this.addToWishlistEvent();
      if (this.querySelector("svg"))
        this.querySelector("svg").style.fill = "none";
    }
  }
}

customElements.define("wish-heart", WishHeart);
